import * as React from 'react'

import { usePopoverState } from '../popover'
import { Tooltip, TooltipTrigger, useTooltipState } from '../tooltip'
import { Paragraph } from '../typography'
import { SelectProps, StatelessSelect } from './select'
import { SelectItem } from './types'

export interface SelectWithTooltipProps<ItemType, ValueType, Name> extends SelectProps<ItemType, ValueType, Name> {
  /**
   * Labels which renders in tooltip on hover
   */
  tooltipLabel?: string
}

export function SelectWithTooltip<ValueType, ItemType extends SelectItem<ValueType>, Name = never>({
  children,
  tooltipLabel,
  popoverStateSettings,
  isNativeMobileSelect = false,
  ...props
}: SelectWithTooltipProps<ItemType, ValueType, Name>) {
  const popoverState = usePopoverState(popoverStateSettings)
  const tooltip = useTooltipState({ placement: 'bottom', unstable_timeout: 300 })
  const isTooltipIgnored = React.useRef(false)

  /**
   * Ignore focus re-trigger (do not show tooltip) when page visibility/focus state changes
   *  - some browsers re-trigger focus on `activeElement` when document becomes visible
   */
  React.useEffect(() => {
    const onChange = () => {
      if (
        document.visibilityState === 'visible' &&
        document.activeElement === popoverState.unstable_referenceRef.current
      ) {
        isTooltipIgnored.current = true
        tooltip.hide()
      }
    }

    document.addEventListener('visibilitychange', onChange)
    window.addEventListener('blur', onChange)
    return () => {
      document.removeEventListener('visibilitychange', onChange)
      window.removeEventListener('blur', onChange)
    }
    // Changing this to `tooltip` dependency will run this effect on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popoverState.unstable_referenceRef, tooltip.hide])

  // hide tooltip when select dropdown opens
  React.useEffect(() => {
    if (popoverState.visible) {
      isTooltipIgnored.current = true
      tooltip.hide()
    }
  }, [popoverState.visible, tooltip])

  const onSelectFocus = React.useRef(() => {
    if (!isTooltipIgnored.current) tooltip.show()
  })

  // Make sure the tooltip doesn’t reopen when the select is closed until focused again
  const onSelectBlur = React.useRef(() => {
    isTooltipIgnored.current = popoverState.visible
    if (!isTooltipIgnored.current) tooltip.hide()
  })

  return (
    <label className="relative block w-full">
      <StatelessSelect
        {...props}
        popoverState={popoverState}
        onFocus={onSelectFocus.current}
        onBlur={onSelectBlur.current}
        isNativeMobileSelect={isNativeMobileSelect}
      >
        {children}
      </StatelessSelect>
      {isNativeMobileSelect === false && (
        <>
          <TooltipTrigger
            tabIndex={-1}
            state={tooltip}
            aria-label={tooltipLabel}
            className="absolute inset-0 cursor-pointer"
          />
          <Tooltip state={tooltip}>
            <Paragraph size="small">{tooltipLabel}</Paragraph>
          </Tooltip>
        </>
      )}
    </label>
  )
}
